import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from "@angular/core";

import {
    BehaviorSubject,
    mergeMap,
    Observable,
    map,
    share,
    tap
} from "rxjs";

import { PaginationOptionsModel } from "../../../shared-library/form-controls/pagination/pagination-options.model";
import { BrowserWindowService } from "../../../shared-library/services/environment/browser-window.service";
import { PaginationOptionsFactoryService } from "../services/pagination-options-factory.service";
import { SearchFilterMetadataModel } from "../models/search-filter-metadata.model";
import { BaseSearchResultsModel } from "../models/base-search-results.model";
import { DeliveryApiService } from "../../../shared-library/delivery-api/delivery-api.service";
import { DirectorySearchPageModel } from "../../../shared-library/delivery-api/models/directory-search-page.model";
import { LockService } from "../services/lock.service";
import { SearchParametersModel } from "./models/search-parameters.model";
import {ContentSearchService} from "./services/content-search.service";
import {ContentSearchParametersFactoryService} from "./services/content-search-parameters-factory.service";
import {ContentSearchConfigurationModel} from "./models/content-search-configuration.model";
import {ContentItemModel} from "./models/content-search.model";
import {ContentSearchFilterOptionModel} from "./models/content-search-filter-option.model";
import {ContentSearchFilterMetadataModel} from "./models/content-search-filter-metadata.model";

@Component({
    selector: "pcg-ng-content-search",
    templateUrl: "./content-search.component.html"
})
export class ContentSearchComponent implements OnInit, OnDestroy {
    public static SELECTOR: string = "pcg-ng-content-search";

    @ViewChild("mapContainer", {static: false})

    public paginationOptions: PaginationOptionsModel;
    public configuration: ContentSearchConfigurationModel;
    protected pageModel$: Observable<DirectorySearchPageModel>;

    protected selectedSection: string;
    protected selectedCategories: Array<string> = [];
    protected selectedPageNumber: number;
    protected selectedSorting: string = "0";
    protected selectedSearchText: string = "";

    public searchFilters$: Observable<Array<SearchFilterMetadataModel>>;
    public contentCategoriesFilters$: Observable<Array<ContentSearchFilterOptionModel>>;
    public searchParameters$: BehaviorSubject<SearchParametersModel>;
    public searchResult$: Observable<BaseSearchResultsModel<ContentItemModel>>;

    @Input() public activeSearchTab: string;
    @Input() public searchContext: string;
    @Output() protected searchTabChange = new EventEmitter<string>();

    public get isLocked$(): Observable<boolean> {
        return this._lockService.isLocked$;
    }

    public constructor(
        private _browserWindowService: BrowserWindowService,
        private _paginationOptionsFactory: PaginationOptionsFactoryService,
        private _searchParametersFactory: ContentSearchParametersFactoryService,
        private _searchService: ContentSearchService,
        private _deliveryApiService: DeliveryApiService,
        private _lockService: LockService
    ) {
    }

    public ngOnInit(): void {
        this.pageModel$ = this._deliveryApiService.getPage$<DirectorySearchPageModel>(window.location.pathname);

        this.paginationOptions = this._paginationOptionsFactory.createDefaultPaginationOptions();
        this.paginationOptions.showGoToPrevious = true;
        this.paginationOptions.showGoToNext = true;

        const initialParameters = this._searchParametersFactory.createFromQueryString(this._browserWindowService.getQueryString());
        this.selectedSorting = initialParameters.sortOption;
        this.selectedSearchText = initialParameters.searchText;
        this.selectedCategories = initialParameters.categories;
        initialParameters.searchTab = this.activeSearchTab;
        initialParameters.searchContext = this.searchContext;

        this.searchParameters$ = new BehaviorSubject<SearchParametersModel>(initialParameters);

        this.searchFilters$ = this._searchService.getTemplateMetadata("00000000-0000-0000-0000-000000000000");
        this.contentCategoriesFilters$ = this._searchService.getSearchFilters().pipe(
            map(filters => {
                const apiCategories = filters.find(f => f.alias === "categories")?.options ?? [];

                const availableCategories = apiCategories.length > 0 ? apiCategories : [
                    { name: "Adult", selected: false },
                    { name: "Children", selected: false }
                ];

                return availableCategories.map(c => ({
                    ...c,
                    selected: this.selectedCategories.includes(c.name)
                }));
            })
        );

        this.searchResult$ = this.searchParameters$.pipe(
            tap(() => this._lockService.lock()),
            tap(parameters => this.setQueryString(parameters)),
            map(parameters => this.mapParameters(parameters)),
            mergeMap(parameters => this._searchService.getSearchResults(parameters)),
            tap(() => this._lockService.unlock()),
            share()
        );
    }

    public ngOnDestroy(): void {
    }

    protected getFilter(alias: string, filters: Array<SearchFilterMetadataModel>): SearchFilterMetadataModel {
        return filters.find(f => f.alias === alias);
    }

    private mapParameters(parameters: SearchParametersModel): SearchParametersModel {
        const mapped = { ...parameters };

        switch (mapped.sortOption) {
            case "Rank;asc":
                mapped.sortOption = "0";
                break;
            case "Name;asc":
                mapped.sortOption = "1";
                break;
            case "Name;desc":
                mapped.sortOption = "2";
                break;
            default:
                break;
        }

        return mapped;
    }

    private setQueryString(parameters: SearchParametersModel): void {
        this._browserWindowService.setQueryString(this._searchParametersFactory.serialiseToQueryString(parameters));
    }

    protected setPageNumber(pageNumber: number): void {
        this.selectedPageNumber = pageNumber;
        const parameters = {...this.searchParameters$.value};
        parameters.pageNumber = pageNumber ?? 1;
        this.searchParameters$.next(parameters);
    }

    protected setSorting(sorting: string): void {
        this.selectedSorting = sorting;
        const parameters = {...this.searchParameters$.value};
        parameters.sortOption = sorting;
        parameters.pageNumber = 1;
        this.searchParameters$.next(parameters);
    }

    protected setSearchText(): void {
        const parameters = { ...this.searchParameters$.value };
        parameters.searchText = this.selectedSearchText?.trim() || "";
        parameters.pageNumber = 1;
        parameters.sortOption = "Rank;asc";
        this.selectedSorting = parameters.sortOption;
        this.searchParameters$.next(parameters);
    }

    public handleSearchTabChange(tab: string): void {
        this.searchTabChange.emit(tab);
    }

    protected setTemplateId(templateId: string): void {
        const currentParameters = this.searchParameters$.value;
        currentParameters.templateId = templateId ? templateId : "";
        currentParameters.pageNumber = 1;
        this.setQueryString(currentParameters);
        this.handleSearchTabChange("directory");
    }

    protected setCategoryFilter(updatedCategories: Array<ContentSearchFilterOptionModel>): void {
        this.selectedCategories = updatedCategories
            .filter(c => c.selected)
            .map(c => c.name);
        const parameters = { ...this.searchParameters$.value };
        parameters.categories = this.selectedCategories;
        this.searchParameters$.next(parameters);
    }
}
